import styles from "./styles.module.css";

/**
 * Public privacy policy page for the Shopify App Store listing.
 *
 * Deliberately outside the embedded /app/* tree and outside
 * `authenticate.admin` -- reviewers, merchants, and shoppers all need to be
 * able to open this without installing the app or having a Shopify session.
 *
 * The data-handling claims below are drawn directly from this app's own
 * services, not boilerplate:
 *  - `app/services/gdpr.server.ts` / `attribution.server.ts`: ScanFlow never
 *    stores a shopper's name, email, phone, or Shopify customer ID --
 *    attribution links a scan to an order ID and revenue only, keyed by an
 *    internal anonymous visitor ID.
 *  - `app/shopify/scopes.ts`: the exact Admin API scopes requested.
 *  - `apphosting.yaml`: hosting on Firebase App Hosting / Google Cloud,
 *    us-central1, with SHOPIFY_API_SECRET in Cloud Secret Manager rather
 *    than plaintext.
 *
 * This is a good-faith draft, not legal advice -- have it reviewed before
 * relying on it for App Store submission.
 */
export default function PrivacyPolicy() {
  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <h1 className={styles.heading}>ScanFlow Privacy Policy</h1>
        <p className={styles.updated}>Last updated: August 14, 2026</p>

        <div className={styles.section}>
          <h2 className={styles.sectionHeading}>Overview</h2>
          <p className={styles.text}>
            ScanFlow is a Shopify app that lets merchants create QR code marketing campaigns and track the sales
            they drive. This policy explains what information ScanFlow collects, how it's used, and how it's
            protected -- both for merchants who install the app and for shoppers who scan a ScanFlow QR code.
          </p>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionHeading}>Information we collect</h2>
          <p className={styles.text}>From merchants who install ScanFlow:</p>
          <ul className={styles.list}>
            <li>Your shop domain and an access token that lets ScanFlow act on your store through Shopify's API.</li>
            <li>
              The campaigns you create -- QR code destinations, names, and the products, collections, pages, or
              discounts you attach to them.
            </li>
            <li>Basic account identifiers for staff who use ScanFlow inside your Shopify admin.</li>
          </ul>
          <p className={styles.text}>From shoppers who scan a ScanFlow QR code:</p>
          <ul className={styles.list}>
            <li>The time of the scan and an internal, randomly generated visitor ID -- never a name or contact detail.</li>
            <li>
              If that scan leads to a paid order, the resulting order ID and revenue amount, matched automatically
              through a URL parameter attached to the redirect. ScanFlow never receives or stores a shopper's name,
              email address, phone number, or Shopify customer ID.
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionHeading}>Shopify permissions we request</h2>
          <p className={styles.text}>ScanFlow requests the minimum Shopify Admin API access it needs to work:</p>
          <ul className={styles.list}>
            <li>
              <strong>read_products</strong> -- to let you pick a product or collection as a QR code destination.
            </li>
            <li>
              <strong>read_content</strong> -- to let you pick a page or blog article as a QR code destination.
            </li>
            <li>
              <strong>read_discounts</strong> -- to let you attach an existing discount to a campaign.
            </li>
            <li>
              <strong>read_orders</strong> -- to match paid orders back to the scan that drove them, for revenue
              reporting.
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionHeading}>How we store and protect data</h2>
          <p className={styles.text}>
            ScanFlow's data is stored on Google Cloud (Firebase) infrastructure in the us-central1 region, in a
            project used exclusively for ScanFlow. Access is restricted through Google Cloud IAM, and sensitive
            credentials such as your store's API secret are kept in Google Cloud Secret Manager rather than stored
            as plain text.
          </p>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionHeading}>Data retention and deletion</h2>
          <p className={styles.text}>
            We keep your campaign and analytics data for as long as ScanFlow is installed on your store. When you
            uninstall ScanFlow, Shopify notifies us and we permanently delete all data associated with your store --
            campaigns, QR codes, scan records, analytics, reports, and your access token -- typically within 48
            hours.
          </p>
          <p className={styles.text}>
            Because ScanFlow never stores a shopper's name, email, phone number, or Shopify customer ID, there is no
            shopper-identifying data to export or erase when Shopify's data request or redaction webhooks fire for
            an individual shopper.
          </p>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionHeading}>Third parties</h2>
          <p className={styles.text}>
            ScanFlow shares data only with Shopify (the platform it's built on) and Google Cloud / Firebase (its
            hosting and database provider). We do not sell data, and we do not share it with advertisers or other
            third parties.
          </p>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionHeading}>Your rights</h2>
          <p className={styles.text}>
            Merchants can remove all of their ScanFlow data at any time by uninstalling the app. Shoppers with
            questions about a store's use of ScanFlow should contact that store directly -- ScanFlow has no way to
            identify or contact individual shoppers, since it never collects their personal information.
          </p>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionHeading}>Changes to this policy</h2>
          <p className={styles.text}>
            If this policy changes, we'll update the date at the top of this page.
          </p>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionHeading}>Contact us</h2>
          <p className={styles.text}>
            Questions about this policy or ScanFlow's data practices can be sent to{" "}
            <a className={styles.link} href="mailto:cygentechnologieslimited@gmail.com">
              cygentechnologieslimited@gmail.com
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
